import { 
  Title, 
  Text, 
  Paper, 
  Stack, 
  Card, 
  Avatar, 
  Group, 
  Badge, 
  Button, 
  Grid, 
  RingProgress, 
  ThemeIcon,
  Divider,
  Accordion,
  ScrollArea,
  TextInput,
  ActionIcon,
  Loader,
  List,
  CopyButton,
  Tooltip,
  rem,
  UnstyledButton,
  Box
} from '@mantine/core';
import { IconMessageCircle2, IconArrowUp, IconInfoCircle, IconUser, IconSend, IconRefresh, IconClock, IconCopy, IconCheck, IconEye, IconChevronDown } from '@tabler/icons-react';
import { useState, useEffect } from 'react';

import { AnalysisData, FeedbackItem, AudiencePersona, FormData, Message, Improvement } from '@/types';
import { generatePersonaChatResponse, generateImprovements, generateBestTimeToPost } from '@/api/analysis';
import logger from '@/utils/logger';

interface AudienceFeedbackProps {
  analysisData: AnalysisData;
  formData: FormData;
  inlineChats: Record<string, boolean>;
  onToggleInlineChat: (personaId: string) => void;
  onViewImprovements: (improvements: Improvement[]) => void;
  onCreateNewTest: () => void;
}

const AudienceFeedback = ({ 
  analysisData, 
  formData,
  inlineChats,
  onToggleInlineChat,
  onViewImprovements,
  onCreateNewTest
}: AudienceFeedbackProps) => {
  const { overallScore, engagementProbability, feedback, audiencePersonas } = analysisData;
  const [isLoadingImprovements, setIsLoadingImprovements] = useState(false);
  const [bestTimeToPost, setBestTimeToPost] = useState<string | null>(null);
  const [isLoadingBestTime, setIsLoadingBestTime] = useState(true);
  const [isPostExpanded, setIsPostExpanded] = useState(false);
  
  useEffect(() => {
    // Check if there's a mismatch between feedback personas and the personas list
    // This logs any inconsistencies to help with debugging
    const feedbackPersonaIds = new Set(feedback.map(item => item.persona.id));
    const audiencePersonaIds = new Set(audiencePersonas.map(persona => persona.id));
    
    if (feedbackPersonaIds.size !== audiencePersonaIds.size) {
      logger.warn('Mismatch between feedback personas and audience personas', {
        feedbackCount: feedbackPersonaIds.size,
        audienceCount: audiencePersonaIds.size
      });
    }
    
    for (const id of feedbackPersonaIds) {
      if (!audiencePersonaIds.has(id)) {
        logger.warn(`Feedback contains persona ID ${id} which is not in the audiencePersonas list`);
      }
    }
    
    for (const id of audiencePersonaIds) {
      if (!feedbackPersonaIds.has(id)) {
        logger.warn(`Audience personas contains ID ${id} which has no associated feedback`);
      }
    }
  }, [feedback, audiencePersonas]);
  
  // Load best time to post when component mounts
  useEffect(() => {
    const fetchBestTimeToPost = async () => {
      try {
        setIsLoadingBestTime(true);
        const time = await generateBestTimeToPost(formData);
        setBestTimeToPost(time);
        logger.info('Successfully loaded best time to post:', time);
      } catch (error) {
        logger.error('Error fetching best time to post:', error);
        setBestTimeToPost('Weekdays between 9-11 AM');
      } finally {
        setIsLoadingBestTime(false);
      }
    };
    
    fetchBestTimeToPost();
  }, [formData]);
  
  // Color based on score
  const getScoreColor = (score: number): string => {
    if (score >= 85) return 'teal';
    if (score >= 70) return 'green';
    if (score >= 60) return 'yellow';
    if (score >= 50) return 'orange';
    return 'red';
  };
  
  const scoreColor = getScoreColor(overallScore);
  
  // Function to handle generating improvements when the user clicks the button
  const handleViewImprovements = async () => {
    logger.info('User requested improvement suggestions - starting generation process');
    setIsLoadingImprovements(true);
    
    // Immediately move to the improvements view with "loading" state
    // This gives users faster feedback that their action was registered
    const pendingImprovements = [
      {
        id: 'loading-1',
        type: 'content' as 'content',
        suggestion: 'Generating suggestions...',
        impact: 'high' as 'high'
      }
    ];
    
    // Start improvements generation in background
    onViewImprovements(pendingImprovements);
    
    try {
      // Generate improvements based on feedback
      logger.info(`Generating improvements based on ${feedback.length} feedback items`);
      const improvements = await generateImprovements(formData, feedback);
      logger.info(`Successfully generated ${improvements.length} improvement suggestions`);
      
      // Update improvements once they're ready
      onViewImprovements(improvements);
    } catch (error) {
      logger.error('Failed to generate improvements:', error);
      // If generation fails, use default improvements to proceed anyway
      logger.warn('Using default improvements after generation failure');
      const defaultImprovements = [
        {
          id: 'imp-default-1',
          type: 'content' as 'content',
          suggestion: `Add more specific examples from your experience in ${formData.userIndustry || 'your industry'}.`,
          impact: 'high' as 'high'
        },
        {
          id: 'imp-default-2',
          type: 'format' as 'format',
          suggestion: 'Break your post into shorter paragraphs to improve readability on mobile devices.',
          impact: 'medium' as 'medium'
        },
        {
          id: 'imp-default-3',
          type: 'engagement' as 'engagement',
          suggestion: 'End with a question to encourage more comments from your audience.',
          impact: 'high' as 'high'
        }
      ];
      
      logger.info('Proceeding with default improvements');
      onViewImprovements(defaultImprovements);
    } finally {
      setIsLoadingImprovements(false);
    }
  };
  
  return (
    <Stack gap="xl" className='responsive-container'>
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="xl">
          <Title order={2} ta="center">
            Post Analysis Results
          </Title>
          
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card p="xl" radius="md" withBorder h="100%">
                <Stack gap="md" align="center" justify="space-between" h="100%">
                  <Title order={3} ta="center">Overall Score</Title>
                  
                  <RingProgress
                    size={180}
                    thickness={18}
                    roundCaps
                    sections={[{ value: overallScore, color: scoreColor }]}
                    label={
                      <Text fw={700} ta="center" size="xl">
                        {overallScore}%
                      </Text>
                    }
                  />
                  
                  <Text ta="center" c="dimmed">
                    Based on audience feedback
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card p="xl" radius="md" withBorder h="100%">
                <Stack gap="md" justify="space-between" h="100%">
                  <Title order={3} ta="center">Engagement Prediction</Title>
                  
                  <Stack gap="md" align="center">
                    <ThemeIcon size={80} radius={100} color={scoreColor}>
                      <IconArrowUp size={36} />
                    </ThemeIcon>
                    
                    <Title order={1}>{engagementProbability}%</Title>
                    
                    <Text ta="center" c="dimmed">
                      Probability of generating meaningful engagement
                    </Text>
                  </Stack>
                  
                  <Text ta="center" size="sm" c="dimmed">
                    Engagement includes likes, comments, shares, and profile views
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
          
          {/* See Your Post */}
          <Card p="0" withBorder>
            <UnstyledButton 
              onClick={() => setIsPostExpanded(!isPostExpanded)} 
              style={{ width: '100%', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Group gap="xs">
                <IconEye size={16} />
                <Text fw={500}>See Your Post</Text>
              </Group>
              <IconChevronDown 
                size={16} 
                style={{ transform: isPostExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }}
              />
            </UnstyledButton>
            
            {isPostExpanded && (
              <Box p="md">
                <Stack gap="md">
                  <Card p="md" withBorder>
                    <Stack gap="md">
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{formData.postContent}</Text>
                      {formData.attachments && formData.attachments.length > 0 && (
                        <Text size="sm" c="dimmed" fs="italic">
                          Post includes {formData.attachments.map(a => a.type).join(', ')} attachment(s)
                        </Text>
                      )}
                    </Stack>
                  </Card>
                  <Group justify="flex-end">
                    <CopyButton value={formData.postContent} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy to clipboard'} withArrow position="top">
                          <Button 
                            color={copied ? 'teal' : 'blue'} 
                            leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                            onClick={copy}
                          >
                            {copied ? 'Copied' : 'Copy Post'}
                          </Button>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Stack>
              </Box>
            )}
          </Card>
          
          {/* Best Time to Post Card */}
          <Card p="xl" radius="md" withBorder>
            <Group gap="md">
              <ThemeIcon size={48} radius="md" color="blue">
                <IconClock size={24} />
              </ThemeIcon>
              
              <Stack gap="xs" style={{ flex: 1 }}>
                <Title order={4}>Best Time to Post</Title>
                {isLoadingBestTime ? (
                  <Group>
                    <Loader size="sm" />
                    <Text>Calculating optimal posting time...</Text>
                  </Group>
                ) : (
                  <Text size="lg" fw={500}>
                    {bestTimeToPost}
                  </Text>
                )}
              </Stack>
            </Group>
          </Card>
          
          <Divider />
          
          <Stack gap="md">
            <Title order={3}>Audience Feedback</Title>
            <Text c="dimmed">
              Below is feedback from an audience matching your target demographics.
            </Text>
            
            <Stack gap="md">
              {feedback.map((item) => (
                <FeedbackCard 
                  key={item.id} 
                  feedback={item}
                  formData={formData}
                  showChat={inlineChats[item.persona.id] || false}
                  onToggleChat={() => onToggleInlineChat(item.persona.id)}
                />
              ))}
            </Stack>
          </Stack>
          
          <Group justify="center" gap="md">
            <Button 
              variant="filled" 
              color="blue" 
              size="lg"
              loading={isLoadingImprovements}
              onClick={handleViewImprovements}
              disabled={isLoadingImprovements}
            >
              {isLoadingImprovements 
                ? 'Generating Suggestions...' 
                : 'View Improvement Suggestions'}
            </Button>
            <Button 
              variant="outline" 
              color="gray" 
              size="lg"
              leftSection={<IconRefresh size={20} />}
              onClick={onCreateNewTest}
            >
              Start New Test
            </Button>
          </Group>
          
          <Divider />
          
          <Card withBorder p="md" radius="md" bg="blue.0">
            <Group align="flex-start" wrap="nowrap">
              <ThemeIcon color="blue" size="lg" variant="light" radius="xl">
                <IconInfoCircle size={20} />
              </ThemeIcon>
              <Stack gap="xs">
                <Text fw={500}>About Your Audience</Text>
                <Text size="sm">
                  We've created {audiencePersonas.length} industry-relevant personas based on your professional background and target audience. 
                  These personas represent professionals who would be interested in your content, with varied experience levels and industry backgrounds.
                </Text>
                <Accordion variant="contained" radius="md">
                  <Accordion.Item value="personas">
                    <Accordion.Control icon={<IconUser size={18} />}>
                      <Text size="sm">View Audience Personas in Detail</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="md">
                        {audiencePersonas.map(persona => (
                          <Card key={persona.id} withBorder p="sm" radius="md">
                            <Stack gap="sm">
                              <Group wrap="nowrap" align="flex-start">
                                <Avatar size="lg" src={persona.avatar} radius="xl" />
                                <Stack gap={0} style={{ flex: 1 }}>
                                  <Text fw={600}>{persona.name}</Text>
                                  <Text size="sm" c="dimmed">{persona.title} • {persona.industry}</Text>
                                  {persona.personality && (
                                    <Text size="sm" mt="xs" fs="italic">"{persona.personality}"</Text>
                                  )}
                                </Stack>
                              </Group>
                              
                              {persona.interests.length > 0 && (
                                <Stack gap={5}>
                                  <Text size="sm" fw={500}>Professional Interests:</Text>
                                  <List size="sm" spacing="xs" withPadding>
                                    {persona.interests.map((interest, index) => (
                                      <List.Item key={index}>{interest}</List.Item>
                                    ))}
                                  </List>
                                </Stack>
                              )}
                            </Stack>
                          </Card>
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Stack>
            </Group>
          </Card>
        </Stack>
      </Paper>
    </Stack>
  );
};

interface FeedbackCardProps {
  feedback: FeedbackItem;
  formData: FormData;
  showChat: boolean;
  onToggleChat: () => void;
}

const FeedbackCard = ({ feedback, formData, showChat, onToggleChat }: FeedbackCardProps) => {
  const { persona, score, comment } = feedback;
  const scoreColor = score >= 80 ? 'green' : score >= 60 ? 'yellow' : 'red';
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Message[]>([
    {
      id: `welcome-${persona.id}`,
      text: `Hello! I'm ${persona.name}. What would you like to know about my feedback on your post?`,
      sender: 'persona',
      timestamp: new Date()
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  
  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: message,
      sender: 'user',
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsTyping(true);
    
    try {
      logger.info(`Generating chat response for message to persona: ${persona.name}`);
      // Call the actual API with the current chat context
      const responseText = await generatePersonaChatResponse(
        persona,
        userMessage.text,
        chatMessages,
        formData
      );
      
      logger.info(`Received chat response for persona: ${persona.name}`);
      
      // Add persona response
      setChatMessages(prev => [
        ...prev,
        {
          id: `persona-${Date.now()}`,
          text: responseText,
          sender: 'persona',
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      logger.error(`Failed to get persona chat response for ${persona.name}:`, error);
      
      // Add error message
      setChatMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          text: 'Sorry, I had trouble processing your message. Could you try again?',
          sender: 'persona',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  return (
    <Card p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group wrap="nowrap" justify="space-between">
          <Group wrap="nowrap">
            <Avatar src={persona.avatar} size={50} radius="xl" />
            
            <Stack gap={5}>
              <Text fw={500}>{persona.name}</Text>
              <Group gap={5}>
                <Text size="xs" c="dimmed">{persona.title}</Text>
                <Text size="xs" c="dimmed">•</Text>
                <Text size="xs" c="dimmed">{persona.industry}</Text>
              </Group>
            </Stack>
          </Group>
          
          <Badge size="lg" color={scoreColor}>{score}% Relevant</Badge>
        </Group>
        
        <Text>{comment}</Text>
        
        <Group>
          <Button 
            variant="subtle" 
            size="xs"
            leftSection={<IconMessageCircle2 size={16} stroke={1.5} />}
            onClick={onToggleChat}
          >
            {showChat ? 'Hide Reply' : 'Reply'}
          </Button>
        </Group>
        
        {showChat && (
          <Card p="md" radius="md" withBorder bg="gray.0">
            <Stack gap="md">
              <ScrollArea h={200} offsetScrollbars type="auto">
                <Stack gap="md" p="xs">
                  {chatMessages.map(message => (
                    <Group
                      key={message.id}
                      align="flex-start"
                      justify={message.sender === 'user' ? 'flex-end' : 'flex-start'}
                    >
                      {message.sender === 'persona' && (
                        <Avatar src={persona.avatar} size="sm" radius="xl" />
                      )}
                      
                      <Card
                        p="sm"
                        radius="md"
                        bg={message.sender === 'user' ? 'blue.5' : 'gray.0'}
                        c={message.sender === 'user' ? 'white' : 'dark'}
                        style={{ 
                          maxWidth: '80%',
                          marginLeft: message.sender === 'user' ? 'auto' : 0,
                          marginRight: message.sender === 'persona' ? 'auto' : 0
                        }}
                      >
                        <Text size="sm">{message.text}</Text>
                      </Card>
                      
                      {message.sender === 'user' && (
                        <Avatar color="blue" radius="xl" size="sm">
                          You
                        </Avatar>
                      )}
                    </Group>
                  ))}
                  
                  {isTyping && (
                    <Group align="flex-start">
                      <Avatar src={persona.avatar} size="sm" radius="xl" />
                      <Card p="sm" radius="md" bg="gray.0">
                        <Group gap="xs">
                          <Loader size="xs" />
                          <Text size="sm">Typing...</Text>
                        </Group>
                      </Card>
                    </Group>
                  )}
                </Stack>
              </ScrollArea>
              
              <Group>
                <TextInput
                  placeholder="Type your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{ flex: 1 }}
                  rightSection={
                    <ActionIcon 
                      variant="filled" 
                      size="lg" 
                      radius="xl" 
                      color="blue"
                      disabled={!message.trim() || isTyping}
                      onClick={handleSendMessage}
                    >
                      <IconSend size={18} />
                    </ActionIcon>
                  }
                />
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>
    </Card>
  );
};

export default AudienceFeedback; 